import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, AlertCircle, Loader2, Plus, Trash2, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  Pagination,
  useAuth,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { useJobListQuery } from '../../hooks/useJobQueries';
import { useATSRole } from '../../hooks/useATSRole';
import { JobFilterBar, type JobFilters } from '../../components/JobFilterBar';
import { JobFormDialog } from '../../components/JobFormDialog';
import jobService from '../../services/job.service';
import type { Job, JobStatus } from '../../types';
import { JOB_STATUS_CONFIG, ATS_ROLE_LABELS, ATS_ROLE_COLORS, ATSRole } from '../../types';

// Filter keys for URL params (single-value filters)
const FILTER_KEYS = ['status', 'pipelineId'];
// Array filter keys for multi-select
const ARRAY_FILTER_KEYS = ['positionIds', 'campaignIds'];

// Status badge component
function StatusBadge({ status }: { status: JobStatus }) {
  const config = JOB_STATUS_CONFIG[status];
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-100 text-gray-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };

  const dotClasses = {
    green: 'bg-green-500',
    gray: 'bg-gray-500',
    yellow: 'bg-yellow-500',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotClasses[config.color]}`} />
      {config.label}
    </span>
  );
}

// Chip component for related entities
function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      {label}
    </span>
  );
}

export function JobListPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { t } = useTranslation(['ats', 'common']);
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({
      filterKeys: FILTER_KEYS,
      arrayFilterKeys: ARRAY_FILTER_KEYS,
    });

  // Fetch current user's role for permission-based UI
  const { role: currentUserRole, isLeaderOrAbove } = useATSRole();

  // Form dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Map URL filters to JobFilters type
  const jobFilters: JobFilters = useMemo(
    () => ({
      status: filters.status as string | undefined,
      positionIds: filters.positionIds as string[] | undefined,
      campaignIds: filters.campaignIds as string[] | undefined,
      pipelineId: filters.pipelineId as string | undefined,
    }),
    [filters]
  );

  // Wrapper to convert JobFilters to Record for URL state
  const handleFiltersChange = useCallback(
    (newFilters: JobFilters) => {
      setFilters(newFilters as Record<string, string | string[] | undefined>);
    },
    [setFilters]
  );

  // React Query for Job list with filters
  const {
    data: jobResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useJobListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    status: jobFilters.status,
    positionIds: jobFilters.positionIds,
    campaignIds: jobFilters.campaignIds,
    pipelineId: jobFilters.pipelineId,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const jobs = jobResponse?.data?.items || [];
  const totalItems = jobResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  // Handlers
  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingJob(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: Job) => {
    setFormMode('edit');
    setEditingJob(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingJob(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteClick = useCallback((record: Job) => {
    setSelectedJob(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedJob || !user || !currentWorkspace) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedJob.id));

    try {
      const token = await user.getIdToken();
      await jobService.deleteJob(selectedJob.id, currentWorkspace.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedJob(null);
    } catch (err: unknown) {
      let errorMessage = t('job.toast.deleted', { defaultValue: 'Unknown error occurred' });
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedJob) {
          newSet.delete(selectedJob.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedJob(null);
      setDeleteError(null);
    }
  };

  const handleViewClick = useCallback(
    (record: Job) => {
      navigate(`/ats/jobs/${record.id}`);
    },
    [navigate]
  );

  // Define table columns (memoized to prevent recreation on every render)
  const columns = useMemo<DataTableColumn<Job>[]>(
    () => [
      {
        key: 'code',
        title: t('job.columns.title'),
        width: 120,
        sortable: true,
        render: (value: string, record: Job) => (
          <button
            onClick={() => handleViewClick(record)}
            className="font-medium font-mono text-orange-600 hover:text-orange-700 hover:underline text-left"
          >
            {value}
          </button>
        ),
      },
      {
        key: 'position',
        title: t('job.columns.position'),
        render: (_value, record: Job) => <Chip label={record.position.name} />,
      },
      {
        key: 'campaign',
        title: t('nav.campaigns'),
        render: (_value, record: Job) => <Chip label={record.campaign.name} />,
      },
      {
        key: 'pipeline',
        title: t('job.columns.pipeline'),
        render: (_value, record: Job) =>
          record.pipeline ? (
            <Chip label={record.pipeline.name} />
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        key: 'quantity',
        title: t('job.columnQuantity', { defaultValue: 'Qty' }),
        width: 60,
        sortable: true,
        render: (value: number) => <span className="text-center block">{value}</span>,
      },
      {
        key: 'status',
        title: t('job.columns.status'),
        width: 100,
        render: (value: JobStatus) => <StatusBadge status={value} />,
      },
      {
        key: 'start_date',
        title: t('job.columnStart', { defaultValue: 'Start' }),
        width: 100,
        sortable: true,
        render: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : <span className="text-gray-400">-</span>,
      },
      {
        key: 'created_at',
        title: t('job.columns.createdAt'),
        width: 100,
        sortable: true,
        render: (value: string) => new Date(value).toLocaleDateString(),
      },
    ],
    [handleViewClick, t]
  );

  // Define table actions
  const actions: DataTableAction<Job>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('common:action.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('common:action.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  // Only show full-page spinner on initial load (no cached data)
  if (isLoading && !jobResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="h-8 w-8 text-orange-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('job.pageTitle')}</h1>
              {currentUserRole && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ATS_ROLE_COLORS[currentUserRole as ATSRole].bg} ${ATS_ROLE_COLORS[currentUserRole as ATSRole].text}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full mr-1.5 ${ATS_ROLE_COLORS[currentUserRole as ATSRole].dot}`}
                  />
                  {ATS_ROLE_LABELS[currentUserRole as ATSRole]}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{t('job.pageDescription')}</p>
          </div>
          {/* Show spinner when refetching (but not on initial load) */}
          {isFetching && !isLoading && <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />}
        </div>
        {isLeaderOrAbove && (
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('job.newJob')}
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <JobFilterBar filters={jobFilters} onFiltersChange={handleFiltersChange} />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('job.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={jobs}
          loading={isLoading && !jobResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('job.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('job.deleteTitle', { defaultValue: 'Delete Job' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('job.deleteConfirm', {
                defaultValue: 'Are you sure you want to delete job "{{code}}"? This action cannot be undone.',
                code: selectedJob?.code,
              })}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common:action.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common:state.loading')}
                </>
              ) : (
                t('common:action.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Job Dialog */}
      <JobFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        job={editingJob}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
